import { tokenStorage } from "./tokenStorage";
import { authApi } from "./authApi";
import { syncUserFromToken, userStore } from "entities/user";

const API_URL = process.env.REACT_APP_API_BASE_URL;

const isJsonResponse = (response) =>
  response.headers.get("content-type")?.includes("application/json");

const buildHeaders = (headers = {}) => {
  const accessToken = tokenStorage.getAccessToken();
  const tokenType = tokenStorage.getTokenType();

  return {
    "Content-Type": "application/json",
    ...(accessToken ? { Authorization: `${tokenType} ${accessToken}` } : {}),
    ...headers,
  };
};

const parseResponse = async (response) => {
  if (!isJsonResponse(response)) return null;
  return response.json().catch(() => null);
};

const parseErrorBody = async (response) => {
  const data = await parseResponse(response);
  if (data) return data;

  try {
    const text = await response.text();
    return text ? { detail: text } : null;
  } catch {
    return null;
  }
};

const isAuthEndpoint = (path) => {
  return (
    path === process.env.REACT_APP_AUTH_PATH ||
    path.includes("/login") ||
    path.includes("/refresh")
  );
};

let refreshPromise = null;

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        throw {
          type: "auth",
          status: 401,
          message: "No refresh token",
        };
      }

      const data = await authApi.refresh(refreshToken);

      if (!data?.accessToken) {
        throw {
          type: "auth",
          status: 401,
          message: "No access token in refresh response",
        };
      }

      tokenStorage.setAccessToken(data.accessToken);

      if (data.refreshToken) {
        tokenStorage.setRefreshToken(data.refreshToken);
      }

      if (data.tokenType) {
        tokenStorage.setTokenType(data.tokenType);
      }

      syncUserFromToken();

      return data.accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

const request = async (path, options = {}, retry = true) => {
  let response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: buildHeaders(options.headers),
      signal: options.signal,
    });
  } catch (error) {
    if (error.name === "AbortError") {
      throw error;
    }
    throw {
      type: "network",
      message: "Ошибка сети. Проверьте подключение к интернету.",
      cause: error,
    };
  }

  const data = await parseResponse(response);

  if (
    response.status === 401 &&
    retry &&
    !isAuthEndpoint(path) &&
    !options.skipAuthRefresh
  ) {
    try {
      await refreshAccessToken(options.signal);
      return request(path, options, false);
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }
      tokenStorage.clearTokens();
      userStore.reset();
      throw {
        type: "auth",
        status: 401,
        message: "Сессия истекла. Войдите заново.",
        cause: error,
      };
    }
  }

  if (!response.ok) {
    const errorBody = data || (await parseErrorBody(response));

    throw {
      type: "http",
      status: response.status,
      message:
        errorBody?.detail ||
        errorBody?.message ||
        errorBody?.error ||
        "Ошибка запроса",
      data: errorBody,
    };
  }

  return data;
};

export const apiClient = {
  get: (path, options) => request(path, { ...options, method: "GET" }),
  post: (path, body, options) =>
    request(path, {
      ...options,
      method: "POST",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  put: (path, body, options) =>
    request(path, {
      ...options,
      method: "PUT",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  patch: (path, body, options) =>
    request(path, {
      ...options,
      method: "PATCH",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  delete: (path, options) => request(path, { ...options, method: "DELETE" }),
};
